/**
 * Delivery has two status machines:
 * - customer_orders: … → dispatched → delivered
 * - delivery_assignments / delivery_tracking: offered → assigned → picked_up → in_transit → delivered
 */

export type DeliveryLeg =
  | 'offered'
  | 'assigned'
  | 'picked_up'
  | 'in_transit'
  | 'delivered'
  | 'failed'
  | 'cancelled';

const LEG_RANK: Record<DeliveryLeg, number> = {
  offered: 1,
  assigned: 2,
  picked_up: 3,
  in_transit: 4,
  delivered: 5,
  failed: 5,
  cancelled: 0,
};

function asLeg(raw: string | null | undefined): DeliveryLeg | null {
  if (!raw) return null;
  if (raw === 'failed_unreachable' || raw === 'failed') return 'failed';
  if (raw === 'dispatched') return 'assigned';
  if (
    raw === 'offered' ||
    raw === 'assigned' ||
    raw === 'picked_up' ||
    raw === 'in_transit' ||
    raw === 'delivered' ||
    raw === 'cancelled'
  ) {
    return raw;
  }
  return null;
}

export function resolveDeliveryLeg(
  assignmentStatus?: string | null,
  trackingStatus?: string | null,
): DeliveryLeg | null {
  const a = asLeg(assignmentStatus);
  const t = asLeg(trackingStatus);
  if (a && t) return LEG_RANK[a] >= LEG_RANK[t] ? a : t;
  return a ?? t;
}

export const DELIVERY_LEG_LABEL: Record<DeliveryLeg, string> = {
  offered: 'Buscando repartidor',
  assigned: 'Repartidor al local',
  picked_up: 'Retirado del local',
  in_transit: 'En camino al cliente',
  delivered: 'Entregado',
  failed: 'No entregado',
  cancelled: 'Cancelado',
};

export const DELIVERY_LEG_HINT_MERCHANT: Record<DeliveryLeg, string> = {
  offered: 'Oferta enviada. Esperá a que alguien acepte.',
  assigned: 'Ya aceptó. Prepará el pedido para cuando llegue.',
  picked_up: 'Retiró el pedido. Va hacia el cliente.',
  in_transit: 'Va hacia el cliente.',
  delivered: 'El cliente ya lo tiene.',
  failed: 'El repartidor tuvo un problema en destino.',
  cancelled: 'Esta entrega ya no va.',
};

export function merchantDeliveryBadge(
  orderStatus: string,
  pickupType: string | null | undefined,
  fulfillment: string | null | undefined,
  assignmentStatus?: string | null,
  trackingStatus?: string | null,
): string | null {
  if (pickupType !== 'delivery') return null;
  if (fulfillment === 'platform_rider' || assignmentStatus || trackingStatus) {
    const leg = resolveDeliveryLeg(assignmentStatus, trackingStatus);
    if (leg) return DELIVERY_LEG_LABEL[leg];
  }
  if (orderStatus === 'dispatched') return 'En camino';
  if (orderStatus === 'delivered' || orderStatus === 'picked_up') return 'Entregado';
  return null;
}
